import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wrench,
  CheckCircle2,
  Download,
  FileText,
  Lock,
  Mail,
  Phone,
  ShieldCheck,
} from "lucide-react";
import api, { trackEvent, API } from "../lib/api";
import { money, moneyRange } from "../lib/format";
import SEO from "../components/SEO";

export default function Results() {
  const { id } = useParams();
  const location = useLocation();
  const teaser = location.state?.teaser;

  const [estimate, setEstimate] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState(null);

  // Email gate state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(true);

  useEffect(() => {
    trackEvent("results_viewed", { estimate_id: id });
    // trackEvent is a stable module-level import; safe to omit but linter
    // wants it explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const unlock = async (e) => {
    e?.preventDefault?.();
    if (!email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setError(null);
    setUnlocking(true);
    trackEvent("email_gate_submitted", { estimate_id: id });
    try {
      const res = await api.post("/estimate/unlock", {
        estimate_id: id,
        email,
        name: name || null,
        consent_marketing: consent,
      });
      setEstimate(res.data.estimate);
      setPdfUrl(res.data.pdf_url ? `${API}${res.data.pdf_url.replace("/api", "")}` : null);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setUnlocking(false);
    }
  };

  // ──────────────── Email Gate ────────────────
  if (!estimate) {
    return (
      <div className="bg-grain" data-testid="results-gate-page">
        <SEO title="Your Bathroom Remodel Estimate" path={`/results/${id}`} noIndex />
        <div className="container-x py-12 lg:py-20 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Teaser card */}
            <div data-testid="teaser-card" className="surface-card p-8 lg:p-10 relative overflow-hidden">
              <p className="overline mb-4">Your estimate range</p>
              {teaser ? (
                <>
                  <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                    {money(teaser.total_low)} – {money(teaser.total_high)}
                  </h1>
                  <p className="mt-3 text-ink-muted">
                    Expected:{" "}
                    <span className="font-semibold text-ink">
                      {money(teaser.total_expected)}
                    </span>
                  </p>
                  <div className="mt-8 grid grid-blueprint grid-cols-2 text-sm">
                    <div className="p-4 bg-surface">
                      <p className="overline mb-1.5">Timeline</p>
                      <p className="font-mono text-base">
                        {teaser.timeline_weeks.low}–
                        {teaser.timeline_weeks.high} weeks
                      </p>
                    </div>
                    <div className="p-4 bg-surface">
                      <p className="overline mb-1.5 text-warn">Hidden costs</p>
                      <p className="font-mono text-base">
                        {teaser.hidden_costs_count} flagged
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-ink-muted">Loading your teaser…</p>
              )}

              <div className="mt-10 relative">
                <div className="absolute inset-x-0 -top-2 h-16 bg-gradient-to-b from-transparent to-surface pointer-events-none" />
                <p className="overline mb-3">What's behind the unlock</p>
                <ul className="text-sm text-ink-muted space-y-2.5">
                  {[
                    "Labor vs materials breakdown",
                    "Itemized hidden-cost warnings",
                    "Per-sq-ft pricing for your ZIP",
                    "Downloadable contractor brief (PDF)",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2.5">
                      <CheckCircle2 size={16} className="text-brand" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Email gate */}
            <form
              onSubmit={unlock}
              data-testid="email-gate-form"
              className="surface-card p-8 lg:p-10"
            >
              <div className="flex items-center gap-3 mb-2">
                <Lock size={18} className="text-brand" />
                <p className="overline">Unlock full estimate</p>
              </div>
              <h2 className="font-display text-3xl font-semibold tracking-tight mt-1">
                Send my detailed breakdown.
              </h2>
              <p className="mt-3 text-ink-muted">
                Enter your email to see the full report, hidden-cost list, and download your printable contractor brief.
              </p>

              <div className="mt-7 space-y-3">
                <input
                  data-testid="input-email-name"
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field"
                />
                <input
                  data-testid="input-email-address"
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                />
              </div>

              <label className="mt-4 flex items-start gap-3 text-sm text-ink-muted cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="checkbox-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-brand"
                />
                <span>
                  Send me occasional planning tips and updates. Unsubscribe anytime.
                </span>
              </label>

              {error && (
                <p className="mt-4 text-sm text-warn" data-testid="gate-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={unlocking}
                data-testid="btn-unlock"
                className="btn-primary w-full mt-6 py-4 text-base"
              >
                {unlocking ? "Unlocking…" : "Show full estimate"}
              </button>

              <p className="mt-4 text-xs text-ink-faint flex items-center gap-2 justify-center">
                <ShieldCheck size={12} /> We never sell your email. One unsubscribe click ends it.
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────── Full Results ────────────────
  return (
    <div data-testid="results-full-page">
      <section className="container-x pt-12 pb-8">
        <p className="overline mb-4" data-testid="results-overline">
          Your bathroom remodel estimate
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-3xl">
          You should plan to spend{" "}
          <span className="text-brand">
            {money(estimate.total_low)}–{money(estimate.total_high)}
          </span>
          .
        </h1>
        <p className="mt-4 text-lg text-ink-muted max-w-2xl">
          Most projects with your scope land around{" "}
          <span className="font-semibold text-ink">
            {money(estimate.total_expected)}
          </span>
          , finishing in about {estimate.timeline_weeks.expected} weeks.
        </p>
      </section>

      {/* RANGE GRID */}
      <section className="container-x mt-4" data-testid="results-range-grid">
        <div className="grid grid-blueprint grid-cols-3">
          <RangeCell label="Low" value={money(estimate.total_low)} />
          <RangeCell label="Expected" value={money(estimate.total_expected)} highlight />
          <RangeCell label="High" value={money(estimate.total_high)} />
        </div>
        <p className="mt-3 font-mono text-xs text-ink-faint">
          Based on {money(estimate.psf_low)}–{money(estimate.psf_high)}/sq ft ·
          Regional multiplier {estimate.region_multiplier}×
        </p>
      </section>

      {/* BREAKDOWN + ACTIONS */}
      <section className="container-x mt-16 grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 surface-card p-8 lg:p-10" data-testid="breakdown-card">
          <p className="overline mb-4">Labor vs materials</p>
          <h2 className="font-display text-2xl font-semibold mb-7">
            Where your money goes at the expected cost
          </h2>
          <div className="space-y-5">
            <BreakdownBar
              label="Labor"
              amount={estimate.labor_cost}
              total={estimate.total_expected}
              color="bg-brand"
            />
            <BreakdownBar
              label="Materials & fixtures"
              amount={estimate.materials_cost}
              total={estimate.total_expected}
              color="bg-ink"
            />
          </div>

          <div className="grid sm:grid-cols-3 grid-blueprint mt-10 text-sm">
            <Cell label="Timeline (low)" value={`${estimate.timeline_weeks.low} wk`} />
            <Cell label="Timeline (expected)" value={`${estimate.timeline_weeks.expected} wk`} highlight />
            <Cell label="Timeline (high)" value={`${estimate.timeline_weeks.high} wk`} />
          </div>
        </div>

        <aside className="surface-card p-7 sticky top-24" data-testid="actions-card">
          <p className="overline mb-3">Next steps</p>
          <h3 className="font-display text-xl font-semibold mb-5">
            Take this to contractors.
          </h3>
          <div className="space-y-3">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-download-pdf"
                onClick={() => trackEvent("pdf_downloaded", { estimate_id: id })}
                className="btn-primary w-full py-3.5"
              >
                <Download size={16} /> Download project brief
              </a>
            )}
            <Link
              to={`/quote?estimate_id=${id}`}
              data-testid="btn-get-quotes"
              onClick={() => trackEvent("quote_cta_clicked", { estimate_id: id })}
              className="btn-ghost w-full py-3.5"
            >
              <Phone size={16} /> Get local quotes
            </Link>
            <p className="flex items-center gap-2 text-xs text-ink-muted pt-3 border-t border-line">
              <Mail size={12} /> A copy is on its way to your inbox.
            </p>
          </div>
        </aside>
      </section>

      {/* HIDDEN COSTS */}
      {estimate.hidden_costs?.length > 0 && (
        <section className="container-x mt-20" data-testid="hidden-costs-section">
          <div className="max-w-2xl mb-8">
            <p className="overline mb-3 text-clay">Hidden cost alerts</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {estimate.hidden_costs.length} surprises to expect once demo starts.
            </h2>
            <p className="mt-3 text-ink-muted">
              Typical exposure: <b className="text-ink">{moneyRange(estimate.hidden_costs_total_low, estimate.hidden_costs_total_high)}</b>. Discuss every one with your contractor before signing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {estimate.hidden_costs.map((h, i) => {
              const tones = ["brand", "clay", "sand"];
              const toneStyles = {
                brand: "border-l-brand bg-brand-tint/40",
                clay: "border-l-clay bg-clay-tint/40",
                sand: "border-l-sand-deep bg-sand-tint/50",
              };
              const toneIcon = {
                brand: "text-brand",
                clay: "text-clay",
                sand: "text-sand-deep",
              };
              const tone = tones[i % 3];
              return (
                <motion.div
                  key={h.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  data-testid={`hidden-cost-${h.id}`}
                  className={`bg-surface border border-line border-l-4 ${toneStyles[tone]} rounded-lg p-6 transition-all hover:-translate-y-1 hover:shadow-md`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span
                      className={`w-11 h-11 grid place-items-center rounded-full bg-surface border border-line ${toneIcon[tone]}`}
                    >
                      <Wrench size={18} strokeWidth={1.75} />
                    </span>
                    <span className="font-mono text-xs px-2.5 py-1 bg-ink/5 text-ink-muted rounded">
                      {h.severity}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl leading-tight mb-2">
                    {h.title}
                  </h3>
                  <p className="font-mono text-sm text-ink mb-3">
                    {moneyRange(h.range[0], h.range[1])}
                  </p>
                  <p className="text-sm text-ink-muted leading-relaxed">
                    {h.blurb}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* PROJECT SUMMARY */}
      <section className="container-x mt-20" data-testid="summary-section">
        <p className="overline mb-3">Project summary</p>
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-8">
          What we estimated against
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 grid-blueprint">
          <Cell label="Size" value={`${estimate.inputs.size_sqft} sq ft`} />
          <Cell label="Remodel type" value={label(estimate.inputs.remodel_type)} />
          <Cell label="Finish level" value={label(estimate.inputs.finish_level)} />
          <Cell label="Primary material" value={label(estimate.inputs.material)} />
          <Cell label="ZIP code" value={estimate.inputs.zip_code || "—"} />
          <Cell
            label="Fixture upgrades"
            value={
              estimate.inputs.fixtures.length
                ? estimate.inputs.fixtures.map(label).join(", ")
                : "None"
            }
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-x my-20" data-testid="results-final-cta">
        <div className="surface-card p-10 lg:p-14 text-center bg-bg-alt">
          <FileText className="mx-auto text-brand mb-5" size={36} />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-xl mx-auto">
            Bring your brief to three contractors.
          </h2>
          <p className="mt-3 text-ink-muted max-w-md mx-auto">
            The fastest way to spot inflated bids and missing line items is side-by-side comparison.
          </p>
          <Link
            to={`/quote?estimate_id=${id}`}
            className="btn-primary mt-7 px-7 py-4"
            data-testid="final-quote-cta"
          >
            Get matched with local contractors
          </Link>
        </div>
      </section>
    </div>
  );
}

const LABELS = {
  cosmetic: "Cosmetic refresh",
  partial: "Partial remodel",
  full_gut: "Full gut remodel",
  budget: "Budget",
  mid: "Mid-range",
  premium: "Premium",
  luxury: "Luxury",
  ceramic: "Ceramic",
  porcelain: "Porcelain",
  natural_stone: "Natural stone",
  marble: "Marble",
  walk_in_shower: "Walk-in shower",
  freestanding_tub: "Freestanding tub",
  double_vanity: "Double vanity",
  heated_floor: "Heated floor",
  smart_toilet: "Smart toilet",
  frameless_glass: "Frameless glass",
  new_lighting: "New lighting",
  exhaust_fan: "Exhaust fan",
};
const label = (k) => LABELS[k] || k;

const RangeCell = ({ label, value, highlight }) => (
  <div className={`p-7 lg:p-10 bg-surface ${highlight ? "bg-brand-tint" : ""}`}>
    <p className="overline mb-3">{label}</p>
    <p className={`font-display text-4xl lg:text-5xl font-semibold tracking-tighter ${highlight ? "text-brand" : ""}`}>
      {value}
    </p>
  </div>
);

const Cell = ({ label, value, highlight }) => (
  <div className={`p-5 bg-surface ${highlight ? "bg-brand-tint" : ""}`}>
    <p className="overline mb-1.5">{label}</p>
    <p className={`font-mono text-sm ${highlight ? "text-brand font-semibold" : "text-ink"}`}>
      {value}
    </p>
  </div>
);

const BreakdownBar = ({ label, amount, total, color }) => {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        <span className="font-mono text-sm">
          {money(amount)}{" "}
          <span className="text-ink-faint">· {pct}%</span>
        </span>
      </div>
      <div className="h-3 bg-bg-alt rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full ${color}`}
        />
      </div>
    </div>
  );
};
